import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { api } from '@/lib/api';
import { useOrgId } from '@/lib/org';
import { NOTIFICATION_LABEL, errorText, type NotificationEvent } from '@/lib/portal';

export const Route = createFileRoute('/settings/notifications')({ component: Notifications });

interface Prefs {
  delivers: boolean;
  preferences: Array<{ event: NotificationEvent; email: boolean }>;
}

/**
 * Notification preferences (gear › Profile › Notifications): which events also reach the
 * signed-in user by email, for the current client. Notifications always show in the top bar.
 */
function Notifications() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const key = ['portal', orgId, 'preferences'];
  const prefs = useQuery({
    queryKey: key,
    queryFn: () => api<Prefs>(`/orgs/${orgId}/notifications/preferences`),
    enabled: Boolean(orgId),
  });
  const save = useMutation({
    mutationFn: (p: { event: NotificationEvent; email: boolean }) =>
      api<Prefs>(`/orgs/${orgId}/notifications/preferences`, {
        method: 'PUT',
        body: JSON.stringify({ preferences: [p] }),
      }),
    onSuccess: (r, p) => {
      qc.setQueryData(key, r);
      toast(`${NOTIFICATION_LABEL[p.event].title}: email ${p.email ? 'on' : 'off'}.`);
    },
    onError: (e) => toast(errorText(e), { tone: 'error' }),
  });
  if (!orgId)
    return <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />;
  return (
    <>
      <PageHeader
        title="Notifications"
        description={
          prefs.data && !prefs.data.delivers
            ? 'Choose which events also reach you by email for this client. Notifications always appear under the bell in the top bar. This server keeps emails on Sent emails and does not deliver them.'
            : 'Choose which events also reach you by email for this client. Notifications always appear under the bell in the top bar.'
        }
      />
      {prefs.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : prefs.isError ? (
        <EmptyState title="Could not load your preferences" hint={prefs.error.message} />
      ) : !prefs.data.preferences.length ? (
        <EmptyState
          title="Nothing to set"
          hint="Client viewers see notifications in the app only."
        />
      ) : (
        <section
          className="max-w-2xl rounded-lg border bg-card"
          data-testid="notification-preferences"
        >
          <div className="grid grid-cols-[1fr_80px] gap-2 border-b px-5 py-3 text-sm font-medium text-muted-foreground">
            <span>Event</span>
            <span className="text-center">Email</span>
          </div>
          <ul className="divide-y">
            {prefs.data.preferences.map((p) => (
              <li key={p.event} className="grid grid-cols-[1fr_80px] items-center gap-2 px-5 py-3">
                <div>
                  <div className="text-sm font-medium">{NOTIFICATION_LABEL[p.event].title}</div>
                  <div className="text-xs text-muted-foreground">
                    {NOTIFICATION_LABEL[p.event].hint}
                  </div>
                </div>
                <div className="text-center">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    aria-label={`Email for ${NOTIFICATION_LABEL[p.event].title}`}
                    checked={p.email}
                    disabled={save.isPending}
                    onChange={(e) => save.mutate({ event: p.event, email: e.target.checked })}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
